/** Global keyboard shortcuts and the "?" help dialog. */
import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { db } from '@/db/dexie';
import * as act from '@/data/actions';
import { useUI } from '@/state/ui';
import { Dialog, DialogContent } from '@/components/ui/overlay';
import { Kbd } from '@/components/ui/button';

export const SHORTCUTS: [string, string][] = [
  ['Ctrl K', 'Search, jump or add'],
  ['N', 'New task'],
  ['Enter', 'Open the focused task'],
  ['Esc', 'Close'],
  ['J / K', 'Move down / up in a list'],
  ['C', 'Complete the focused task'],
  ['D', 'Set the due date'],
  ['S', 'Snooze 1 hour'],
  ['Ctrl 1 – 4', 'Priority: urgent, high, normal, low'],
  ['Ctrl 0', 'Clear priority'],
  ['G then M', 'Go to My Tasks'],
  ['G then I', 'Go to Inbox'],
  ['G then C', 'Go to Calendar'],
  ['?', 'Show these shortcuts'],
];

function typing(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  if (!el) return false;
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

function focusedTaskId(): string | null {
  const el = document.activeElement?.closest('[data-task-id]') as HTMLElement | null;
  return el?.dataset.taskId ?? useUI.getState().focusedTaskId;
}

function moveFocus(delta: number) {
  const rows = Array.from(document.querySelectorAll<HTMLElement>('[role="row"][data-task-id]'));
  if (rows.length === 0) return;
  const current = document.activeElement?.closest('[role="row"][data-task-id]');
  const i = current ? rows.indexOf(current as HTMLElement) : -1;
  const next = rows[Math.max(0, Math.min(rows.length - 1, i + delta))];
  next?.focus();
  next?.scrollIntoView({ block: 'nearest' });
}

export function GlobalShortcuts() {
  const navigate = useNavigate();
  const gPressed = useRef(0);
  const open = useUI((s) => s.shortcutsOpen);
  const setOpen = useUI((s) => s.setShortcutsOpen);

  useEffect(() => {
    const onKey = async (e: KeyboardEvent) => {
      const ui = useUI.getState();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        ui.setPaletteOpen(!ui.paletteOpen);
        return;
      }
      if (typing(e)) return;
      const dialogOpen = Boolean(document.querySelector('[role="dialog"]'));
      const priorityKey = e.ctrlKey && /^[0-4]$/.test(e.key);
      if (dialogOpen && !priorityKey) return;
      const openTaskId =
        new URLSearchParams(window.location.search).get('task') ??
        /^\/t\/([^/]+)/.exec(window.location.pathname)?.[1] ??
        null;
      const id = dialogOpen ? openTaskId : focusedTaskId();
      if (e.ctrlKey && /^[0-4]$/.test(e.key) && id) {
        e.preventDefault();
        const task = await db().tasks.get(id);
        if (task)
          await act.setPriorityWithToast(
            task,
            e.key === '0' ? null : (Number(e.key) as 1 | 2 | 3 | 4),
          );
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (Date.now() - gPressed.current < 1200) {
        gPressed.current = 0;
        const k = e.key.toLowerCase();
        if (k === 'm') void navigate({ to: '/' });
        if (k === 'i') void navigate({ to: '/inbox' });
        if (k === 'c') void navigate({ to: '/calendar' });
        return;
      }
      switch (e.key) {
        case 'n':
        case 'N':
          e.preventDefault();
          ui.openQuickAdd({ tab: 'task' });
          return;
        case '?':
          e.preventDefault();
          setOpen(true);
          return;
        case 'g':
          gPressed.current = Date.now();
          return;
        case 'j':
          e.preventDefault();
          moveFocus(1);
          return;
        case 'k':
          e.preventDefault();
          moveFocus(-1);
          return;
      }
      if (!id) return;
      const task = await db().tasks.get(id);
      if (!task) return;
      if (e.key === 'c') {
        e.preventDefault();
        await act.completeWithToast(task);
      } else if (e.key === 's') {
        e.preventDefault();
        await act.snoozeWithToast(task, '1h');
      } else if (e.key === 'd') {
        e.preventDefault();
        (
          document.querySelector(
            `[data-task-id="${id}"] [aria-label="Due date"]`,
          ) as HTMLElement | null
        )?.click();
      }
    };
    const handler = (e: KeyboardEvent) => void onKey(e);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate, setOpen]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent title="Keyboard shortcuts" width={460}>
        <ul className="grid gap-1 px-4 pb-4">
          {SHORTCUTS.map(([k, d]) => (
            <li
              key={k}
              className="flex h-8 items-center justify-between gap-4 border-b border-line last:border-0"
            >
              <span className="text-ink-2">{d}</span>
              <span className="flex gap-1">
                {k.split(' ').map((part, i) =>
                  part === '/' || part === 'then' || part === '–' ? (
                    <span key={i} className="text-cap text-ink-3">
                      {part}
                    </span>
                  ) : (
                    <Kbd key={i}>{part}</Kbd>
                  ),
                )}
              </span>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
